import { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Icon } from '../ui/Icon';
import { Button } from '../ui/Button';
import { SearchModal } from '../ui/SearchModal';
import { useNavigation } from '../../hooks/useNavigation';
import { cn } from '../../utils/cn';
import { sectorsData } from '../../data/sectors';

import logo from '../../assets/logo.png';

export default function Header() {
  const { goTo } = useNavigation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleContactClick = () => {
    goTo('/contact');
  };

  const handleDropdownEnter = (label: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveDropdown(label);
  };

  const handleDropdownLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 300); // 300ms delay before closing
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navigationItems = [
    { label: 'Home', href: '/' },
    { label: 'Sectors', href: '#', hasDropdown: true },
    { label: 'About', href: '/about' },
    { label: 'Coffee', href: '/coffee' },
  ];

  const sectorItems = Object.values(sectorsData).map(sector => ({
    label: sector.title,
    href: `/sectors/${sector.id}`,
    icon: sector.icon
  }));

  return (
    <header
      className={cn(
        "fixed z-50 border flex items-center",
        // Position & Layout
        "left-1/2 -translate-x-1/2",
        isScrolled
          ? "top-4 w-[calc(100%-2rem)] md:w-[calc(100%-4rem)] max-w-7xl glass border-gray-200/50 rounded-2xl py-2 shadow-xl shadow-gray-200/50"
          : "top-0 w-full bg-white/40 backdrop-blur-sm border-transparent py-4 shadow-none"
      )}
      style={{
        transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Gradient overlay for glass effect */}
      {isScrolled && (
        <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-gray-50/30 rounded-2xl pointer-events-none transition-opacity duration-500" />
      )}
      <div className="w-full h-full px-6 flex items-center justify-between relative z-10">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img src={logo} alt="EasyBuy General Trading" className="h-10 w-auto" style={{ imageRendering: 'crisp-edges' }} />
        </Link>

        {/* Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navigationItems.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.hasDropdown && handleDropdownEnter(item.label)}
              onMouseLeave={handleDropdownLeave}
            >
              {item.hasDropdown ? (
                <button
                  className={cn(
                    'text-sm font-medium transition-colors duration-200',
                    'flex items-center gap-1 cursor-default',
                    activeDropdown === item.label ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  {item.label}
                  <Icon
                    name="chevron-down"
                    className={cn("w-4 h-4 transition-transform duration-200", activeDropdown === item.label ? 'rotate-180' : '')}
                    aria-hidden="true"
                  />
                </button>
              ) : (
                <NavLink
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      'text-sm font-medium transition-colors duration-200',
                      'flex items-center gap-1',
                      isActive
                        ? 'text-gray-900'
                        : 'text-gray-600 hover:text-gray-900'
                    )
                  }
                >
                  {item.label}
                </NavLink>
              )}

              {/* Dropdown Menu */}
              {item.hasDropdown && (
                <div
                  className={cn(
                    "absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden py-2 transition-all duration-300 ease-in-out transform origin-top",
                    activeDropdown === item.label
                      ? "opacity-100 translate-y-0 pointer-events-auto"
                      : "opacity-0 -translate-y-2 pointer-events-none"
                  )}
                >
                  {sectorItems.map((sector) => (
                    <Link
                      key={sector.href}
                      to={sector.href}
                      onClick={() => setActiveDropdown(null)}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group"
                    >
                      <span className="p-2 bg-gray-50 rounded-lg group-hover:bg-orange-50 text-gray-500 group-hover:text-orange-500 transition-colors">
                        <Icon name={sector.icon} className="w-4 h-4" />
                      </span>
                      <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                        {sector.label}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-4">
          {/* Search Button */}
          <button
            onClick={() => setIsSearchOpen(true)}
            className="hidden md:flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Icon name="search" className="w-4 h-4" />
            <span className="hidden lg:inline">Search</span>
            <kbd className="hidden lg:inline px-1.5 py-0.5 text-xs bg-white border border-gray-300 rounded">⌘K</kbd>
          </button>

          <Button
            variant="primary"
            size="sm"
            onClick={handleContactClick}
            className="hidden md:flex bg-gray-900 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-gray-800 transition-transform hover:scale-105 shadow-md"
          >
            Let's Talk
          </Button>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-600 hover:text-gray-900 transition-colors"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'x' : 'menu'} className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      {/* Mobile Menu Overlay */}
      <div
        className={cn(
          "fixed inset-x-0 bg-white border-t border-gray-100 shadow-xl transition-all duration-300 ease-in-out md:hidden overflow-hidden",
          isScrolled ? "top-[60px]" : "top-[80px]",
          isMobileMenuOpen ? (isScrolled ? "h-[calc(100vh-60px)]" : "h-[calc(100vh-80px)]") + " opacity-100" : "h-0 opacity-0"
        )}
      >
        <div className="h-full overflow-y-auto px-6 py-8 flex flex-col gap-6">
          {navigationItems.map((item) => (
            <div key={item.label} className="border-b border-gray-50 pb-4 last:border-0 last:pb-0">
              {item.hasDropdown ? (
                <div className="space-y-4">
                  <div className="font-medium text-gray-900">{item.label}</div>
                  <div className="grid grid-cols-2 gap-3 pl-4">
                    {sectorItems.map((sector) => (
                      <Link
                        key={sector.href}
                        to={sector.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-500 py-1"
                      >
                        <Icon name={sector.icon} className="w-4 h-4" />
                        {sector.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "block text-lg font-medium transition-colors",
                      isActive ? "text-orange-500" : "text-gray-900"
                    )
                  }
                >
                  {item.label}
                </NavLink>
              )}
            </div>
          ))}
          <div className="pt-4">
            <Button
              variant="primary"
              size="lg"
              onClick={() => {
                handleContactClick();
                setIsMobileMenuOpen(false);
              }}
              className="w-full bg-gray-900 text-white justify-center"
            >
              Let's Talk
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
